import { createClient } from "@spork/s4p-server-sdk";
import { createConnection } from "@spork/feathers-client";
import { Platform } from "react-native";

import { getConfig } from "@/getConfig";
import { notifyUnauthorized } from "@/lib/auth/unauthorizedHandler";
import { notifyUpgradeRequired } from "@/lib/auth/upgradeRequiredHandler";
import { getNativeAppVersion } from "@/lib/appVersion";
import { log } from "@/logger";

import { authStorage } from "./storage";
import { JwtAuthenticationClient } from "./JwtAuthenticationClient";

let client: ReturnType<typeof createClient> | null = null;

function isUnauthorizedError(error: unknown): boolean {
    return (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        (error as { code: unknown }).code === 401
    );
}

function isUpgradeRequiredError(error: unknown): boolean {
    return (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        (error as { code: unknown }).code === 426
    );
}

export function getFeathersClient() {
    if (!client) {
        const serverUrl = getConfig("s4pServerUrl");
        if (!serverUrl) {
            throw new Error("s4pServerUrl is not configured");
        }

        const appVersion = getNativeAppVersion();
        const connection = createConnection(serverUrl, true, {
            ackTimeout: 10000,
            ...(appVersion ? { query: { app_version: appVersion } } : {}),
        });
        client = createClient(connection, {
          storage: authStorage,
          Authentication: JwtAuthenticationClient,
        });

        // Sign the user out when any API call returns 401.
        // AuthProvider ignores the callback when the user is not signed in
        // (e.g. failed login), so authentication create is safe to include.
        client.hooks({
            error: {
                all: (context) => {
                    if (isUnauthorizedError(context.error)) {
                        notifyUnauthorized();
                    } else if (
                        Platform.OS !== "web" &&
                        isUpgradeRequiredError(context.error)
                    ) {
                        notifyUpgradeRequired();
                    }
                    return context;
                },
            },
        });
    }

    return client;
}

/** Opens the socket and re-authenticates when the connection dropped. */
export async function ensureFeathersReady(): Promise<void> {
    const client = getFeathersClient();
    if (!client.io.connected) {
        log.debug("Socket is not connected. Opening and re-authenticating.");
        await client.io.open();
        await client.reAuthenticate(true);
    }
}
